<template>
	<div data-tour-scope="layout" class="ly">
		<v-main>
			<v-container fluid class="ly-page">
				<div class="ly-top">
					<div>
						<div class="ly-title">Layout del sito</div>
						<div class="ly-sub">Aspetto e funzioni del sito pubblico. Le modifiche si salvano tutte insieme con il pulsante in basso.</div>
					</div>
					<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
					<v-spacer></v-spacer>
				</div>

				<!-- indice delle sezioni: resta in vista mentre si scorre -->
				<nav class="ly-nav" aria-label="Sezioni">
					<v-chip v-for="s in sezioni" :key="s.id" small :outlined="attiva !== s.id" :color="attiva === s.id ? 'primary' : undefined" class="ly-chip" @click="vaiA(s.id)">
						<v-icon left x-small>{{ s.icona }}</v-icon
						>{{ s.label }}
					</v-chip>
				</nav>

				<div class="ly-sections">
					<SectionCard id="ly-logo" title="Logo e colori" icon="mdi-palette-outline" subtitle="identità del sito">
						<LogoColori :form="form" :dropzone-options="dropzoneOptionsImage" @caricato="uploadCompleteLogo" @invio="sendingEvent" @elimina="deleteDropLogo" />
					</SectionCard>

					<SectionCard id="ly-slider" title="Slider della home" icon="mdi-view-carousel-outline" :subtitle="contaSlide" data-tour="slider">
						<SliderHome
							:form="form"
							:dropzone-options="dropzoneOptionsImage"
							:listitems="listitems"
							:is-loading="isLoadingitem"
							:searchitem.sync="searchitem"
							:searchproduct.sync="searchproduct"
							:create-image="createImage"
							@caricato="uploadCompleteImage"
							@invio="sendingEvent"
							@elimina="deleteDrop"
							@sposta="updateDropSlider"
						/>
					</SectionCard>

					<SectionCard id="ly-home" title="Fasce della home" icon="mdi-view-agenda-outline" :subtitle="contaFasce" data-tour="home">
						<BlocchiHome :blocchi="layout.homepage" :ordine="ordineTema" @sposta="updateDrop" @riordina="ordinaComeTema" />
					</SectionCard>

					<SectionCard id="ly-funzioni" title="Funzioni del sito" icon="mdi-tune-variant" subtitle="si accendono qui, valgono dopo il salvataggio">
						<div class="ly-switches" data-tour="options">
							<div v-for="f in funzioni" :key="f.key" class="ly-switch" :class="{ 'ly-switch--on': form[f.key] }">
								<v-icon :color="form[f.key] ? 'primary' : 'grey'" class="mr-3">{{ f.icona }}</v-icon>
								<div class="ly-switch-text">
									<div class="ly-switch-label">{{ f.label }}</div>
									<div class="ly-muted">{{ f.hint }}</div>
									<div v-if="f.richiede === 'voice' && !voiceDisponibile" class="ly-requisito">
										Si attiva solo dopo aver impostato il modello voce di OpenAI in
										<router-link to="/setting/ai-social">Impostazioni → AI / Social</router-link>
									</div>
									<v-btn-toggle v-if="f.key === 'showProductViews' && form.showProductViews" v-model="form.productViewsPeriod" mandatory dense color="primary" class="mt-2">
										<v-btn small value="today">Oggi</v-btn>
										<v-btn small value="30d">Ultimi 30 giorni</v-btn>
									</v-btn-toggle>
									<v-text-field
										v-if="f.key === 'showLowStock' && form.showLowStock"
										v-model.number="form.lowStockThreshold"
										type="number"
										min="1"
										label="Mostra fino a"
										suffix="unità"
										outlined
										dense
										hide-details
										class="mt-3 ly-soglia"
									/>
								</div>
								<v-switch v-model="form[f.key]" :disabled="f.richiede === 'voice' && !voiceDisponibile" color="primary" hide-details class="mt-0 pt-0 ml-2" />
							</div>
						</div>
					</SectionCard>

					<SectionCard id="ly-popup" title="Popup all'apertura" icon="mdi-message-alert-outline" :subtitle="statoPopup">
						<PopupApertura :popup="form.layout.popup" :editor="editor" :editor-config="editorConfig" @copia="copyTranslatePopup" @traduci="translatePopup" />
					</SectionCard>

					<SectionCard id="ly-cookie" title="Cookie bar" icon="mdi-cookie-outline" :subtitle="statoCookie">
						<CookieBar :cookie="form.layout.cookie_bar" />
					</SectionCard>

					<SectionCard id="ly-tema" title="Tema" icon="mdi-palette-swatch-outline" subtitle="aspetto di header, footer, home, categorie e card prodotto">
						<div data-tour="theme">
							<TemaPreset :value="form.layout.theme.preset" :preset="temi" :personalizzato="temaPersonalizzato" @input="scegliTema" />
						</div>
						<div data-tour="header" class="ly-tema-testi">
							<TestiLingua
								v-if="form.layout.theme.top_header_message"
								:trans="form.layout.theme.top_header_message.trans"
								titolo="Messaggio sopra l'header"
								placeholder="Es. Spedizione gratuita sopra i 49 €"
								@copia="copyTranslate"
								@traduci="translateItem"
							/>
						</div>
						<v-expansion-panels flat class="ly-avanzate" data-tour="variants">
							<v-expansion-panel>
								<v-expansion-panel-header>
									<span><v-icon small class="mr-2">mdi-shield-check-outline</v-icon>Testi dei vantaggi</span>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<div class="ly-muted">
										I quattro vantaggi compaiono nella barra sotto lo slider della home, accanto al pulsante della scheda prodotto e nella scheda «Spedizione e pagamenti». Qui si scrive la descrizione
										di ciascuno; se resta vuota si usa il testo predefinito.
									</div>
									<template v-for="v in vantaggi">
										<TestiLingua
											v-if="form.layout.theme.perks && form.layout.theme.perks[v.key]"
											:key="v.key"
											:trans="form.layout.theme.perks[v.key].trans"
											:titolo="v.label"
											:placeholder="v.predefinito"
											textarea
											@copia="id => copiaVantaggio(v.key, id)"
											@traduci="(id, lang) => traduciVantaggio(v.key, id, lang)"
										/>
									</template>
								</v-expansion-panel-content>
							</v-expansion-panel>
							<v-expansion-panel v-if="usaBoutique && form.layout.theme.boutique">
								<v-expansion-panel-header>
									<span><v-icon small class="mr-2">mdi-hanger</v-icon>Contenuti della home Boutique</span>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<ContenutiBoutique
										:contenuti="form.layout.theme.boutique"
										@carica="openModalUpload"
										@copia="copiaTesto"
										@traduci="traduciTesto"
										@imposta="(blocco, campo, valore) => (form.layout.theme.boutique[blocco][campo] = valore)"
									/>
								</v-expansion-panel-content>
							</v-expansion-panel>
							<v-expansion-panel>
								<v-expansion-panel-header>
									<span><v-icon small class="mr-2">mdi-tune-vertical</v-icon>Avanzate: versione per singola area</span>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<div class="ly-muted mb-4">Il tema imposta tutte le aree insieme; qui si può cambiare la versione di un'area sola.</div>
									<SceltaTema v-model="form.layout.theme.header_type" :opzioni="header_types" titolo="Header" icona="mdi-page-layout-header" />
									<SceltaTema v-model="form.layout.theme.footer_type" :opzioni="footer_types" titolo="Footer" icona="mdi-page-layout-footer" />
									<SceltaTema v-model="form.layout.theme.home_type" :opzioni="area_types" titolo="Home" icona="mdi-home-outline" />
									<SceltaTema v-model="form.layout.theme.category_type" :opzioni="area_types" titolo="Pagina categoria" icona="mdi-view-grid-outline" />
									<SceltaTema v-model="form.layout.theme.card_type" :opzioni="area_types" titolo="Card prodotto" icona="mdi-card-outline" />
									<SceltaTema v-model="form.layout.theme.slider_type" :opzioni="slider_types" titolo="Slider" icona="mdi-view-carousel-outline" />
									<SceltaTema v-model="form.layout.theme.newsletter_type" :opzioni="newsletter_types" titolo="Newsletter" icona="mdi-email-newsletter" />
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</SectionCard>

					<SectionCard id="ly-prodotto" title="Pagina prodotto" icon="mdi-package-variant" subtitle="scelta delle varianti e note" data-tour="product">
						<SceltaTema v-model="form.layout.product.variant" :opzioni="(layout.product && layout.product.variant) || []" titolo="Come si sceglie la variante" icona="mdi-format-list-bulleted-type">
							<TestiLingua
								v-if="form.layout.theme.note_detail_product"
								:trans="form.layout.theme.note_detail_product.trans"
								titolo="Note da mostrare in ogni pagina prodotto"
								textarea
								@copia="copyTranslateNote"
								@traduci="translateItemNote"
							/>
						</SceltaTema>
						<div class="ly-consegna">
							<v-icon color="primary" class="mr-3">mdi-truck-fast-outline</v-icon>
							<div class="ly-switch-text">
								<div class="ly-switch-label">Orario limite per la spedizione in giornata</div>
								<div class="ly-muted">
									Nella scheda prodotto compare «Ordina entro …» e la consegna prevista parte da oggi solo per gli ordini fatti prima di quest'ora (giorni feriali). Vuoto: niente «Ordina entro», la
									consegna prevista parte dal primo giorno feriale.
								</div>
							</div>
							<v-text-field v-model="form.deliveryCutoff" type="time" label="Entro le" clearable outlined dense hide-details class="ly-soglia ml-4" />
						</div>
					</SectionCard>

					<SectionCard id="ly-seo" title="SEO e anteprime" icon="mdi-google" subtitle="come appare il sito su Google e nelle condivisioni" data-tour="seo">
						<CampiLink :valori="form.layout.seo" :chiavi="seo" :etichette="etichetteSeo" :caricabili="['favicon', 'og_image']" @carica="openModalUpload" />
					</SectionCard>

					<SectionCard id="ly-social" title="Social e link" icon="mdi-share-variant-outline" :subtitle="contaSocial" data-tour="social">
						<CampiLink :valori="form.layout.social_links" :chiavi="social_links" :etichette="etichetteSocial" />
					</SectionCard>

					<SectionCard id="ly-script" title="Script e azioni" icon="mdi-code-tags" subtitle="tracciamento e strumenti di terze parti" data-tour="script">
						<ScriptAzioni :form="form" />
					</SectionCard>
				</div>

				<!-- un solo salvataggio per tutta la pagina -->
				<div class="ly-savebar" :class="{ 'ly-savebar--dirty': modificato }">
					<v-icon small :color="modificato ? 'warning' : 'success'" class="mr-2">{{ modificato ? 'mdi-circle-edit-outline' : 'mdi-check-circle-outline' }}</v-icon>
					<span class="ly-savebar-text">{{ modificato ? 'Ci sono modifiche non salvate' : 'Tutto salvato' }}</span>
					<v-spacer></v-spacer>
					<v-btn depressed color="primary" data-tour="save" @click="saveSetting"><v-icon left small>mdi-content-save-outline</v-icon>Salva modifiche</v-btn>
				</div>

				<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout" :vertical="snackbar.mode === 'vertical'">
					{{ snackbar.text }}
					<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
				</v-snackbar>
			</v-container>
		</v-main>
		<v-dialog v-model="dialog_upload" max-width="560">
			<v-card>
				<v-card-title class="ly-dialog-title">
					<v-icon color="primary" class="mr-2">mdi-upload</v-icon>Carica {{ etichetteSeo[mode_upload] ? etichetteSeo[mode_upload].label.toLowerCase() : etichettaCaricamento }}
					<v-spacer></v-spacer>
					<v-btn icon small title="Chiudi" @click="dialog_upload = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="ly-muted mb-2">JPG, GIF, PNG o ICO</div>
					<vue-dropzone id="dropzonemedia" ref="myVueDropzone" :options="dropzoneOptionsImage" @vdropzone-complete="uploadCompleteMedia" @vdropzone-sending="sendingEvent" />
				</v-card-text>
			</v-card>
		</v-dialog>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { ObjectID } from 'bson';
import Draggable from 'vuedraggable';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import SectionCard from '@/components/core/SectionCard.vue';
import LogoColori from '@/components/ecommerce/layout/LogoColori.vue';
import SliderHome from '@/components/ecommerce/layout/SliderHome.vue';
import BlocchiHome from '@/components/ecommerce/layout/BlocchiHome.vue';
import SceltaTema from '@/components/ecommerce/layout/SceltaTema.vue';
import TemaPreset from '@/components/ecommerce/layout/TemaPreset.vue';
import TestiLingua from '@/components/ecommerce/layout/TestiLingua.vue';
import ContenutiBoutique, { BLOCCHI_BOUTIQUE } from '@/components/ecommerce/layout/ContenutiBoutique.vue';
import CampiLink from '@/components/ecommerce/layout/CampiLink.vue';
import ScriptAzioni from '@/components/ecommerce/layout/ScriptAzioni.vue';
import PopupApertura from '@/components/ecommerce/layout/PopupApertura.vue';
import CookieBar from '@/components/ecommerce/layout/CookieBar.vue';
import Vue from 'vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import { useTour } from '@/components/core/tour/useTour';
import { createLayoutTourSteps } from './layout-tour';

import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { slugify } from '../../Helper';
import { shop, product, homepage } from '../../utils/layout';
import Table from '../../components/orders/Table.vue';
import { decode } from 'entities';
import { uuid } from 'vue-uuid';
const FormCreate = () => import('@/components/conditions/Create.vue');
Vue.use(CKEditor);

// versione di ogni area per tema; header, footer, slider e newsletter con i codici che l'ecommerce legge
// (tipo 1 = Harvest, tipo 2 = Bazaar). Le aree nuove hanno il codice del tema; se manca, l'ecommerce usa Harvest.
const AREE_TEMA = {
	harvest: { header_type: 'header_1', footer_type: 'footer_1', slider_type: 'slider_1', newsletter_type: 'newsletter_1' },
	breeze: { header_type: 'breeze', footer_type: 'breeze', slider_type: 'slider_1', newsletter_type: 'newsletter_1' },
	bazaar: { header_type: 'header_2', footer_type: 'footer_2', slider_type: 'slider_2', newsletter_type: 'newsletter_2' },
	boutique: { header_type: 'boutique', footer_type: 'boutique', slider_type: 'slider_1', newsletter_type: 'newsletter_1' },
	forge: { header_type: 'forge', footer_type: 'forge', slider_type: 'slider_1', newsletter_type: 'newsletter_1' },
};
const areeTema = tema => ({ ...AREE_TEMA[tema], home_type: tema, category_type: tema, card_type: tema });
export default {
	name: 'Layout',
	setup() {
		return { tour: useTour(createLayoutTourSteps()) };
	},
	filters: {
		fixed: function (value) {
			if (!+value) return '';
			return _.round(value, 2);
		},
	},
	data() {
		return {
			dialog_upload: false,
			// indice delle sezioni (id della SectionCard)
			sezioni: [
				{ id: 'ly-logo', label: 'Logo e colori', icona: 'mdi-palette-outline' },
				{ id: 'ly-slider', label: 'Slider', icona: 'mdi-view-carousel-outline' },
				{ id: 'ly-home', label: 'Home', icona: 'mdi-view-agenda-outline' },
				{ id: 'ly-funzioni', label: 'Funzioni', icona: 'mdi-tune-variant' },
				{ id: 'ly-popup', label: 'Popup', icona: 'mdi-message-alert-outline' },
				{ id: 'ly-cookie', label: 'Cookie bar', icona: 'mdi-cookie-outline' },
				{ id: 'ly-tema', label: 'Tema', icona: 'mdi-palette-swatch-outline' },
				{ id: 'ly-prodotto', label: 'Pagina prodotto', icona: 'mdi-package-variant' },
				{ id: 'ly-seo', label: 'SEO', icona: 'mdi-google' },
				{ id: 'ly-social', label: 'Social', icona: 'mdi-share-variant-outline' },
				{ id: 'ly-script', label: 'Script', icona: 'mdi-code-tags' },
			],
			attiva: 'ly-logo',
			funzioni: [
				{ key: 'enableWIP', label: 'Sito in manutenzione', hint: 'I visitatori vedono la pagina di manutenzione al posto del sito', icona: 'mdi-hammer-wrench' },
				{ key: 'enablePickupStore', label: 'Ritiro in sede', hint: 'Al checkout compare il ritiro in negozio', icona: 'mdi-storefront-outline' },
				{ key: 'enableCartEnd', label: 'Prodotti a fine carrello', hint: 'Mostra la vetrina di Fine carrello prima del pagamento', icona: 'mdi-cart-plus' },
				{ key: 'enableMultiCurrency', label: 'Multi valuta', hint: 'Il cliente può scegliere la valuta', icona: 'mdi-currency-eur' },
				{ key: 'productPlaceholder', label: 'Testi segnaposto prodotti', hint: 'Ai prodotti senza descrizione mostra un testo finto (lorem ipsum): da spegnere sui siti pubblicati', icona: 'mdi-text-box-outline' },
				{ key: 'notifyWhenAvailable', label: 'Avvisami quando torna disponibile', hint: 'Sui prodotti esauriti il cliente può chiedere un avviso', icona: 'mdi-bell-ring-outline' },
				{
					key: 'notifyPriceDrop',
					label: 'Avvisami se il prezzo scende',
					hint: "Nella scheda prodotto il cliente lascia l'email e riceve un avviso quando il prezzo scende, offerte comprese. Serve il modello email «Notifica quando il prezzo scende»",
					icona: 'mdi-tag-arrow-down-outline',
				},
				{
					key: 'showProductViews',
					label: 'Visualizzazioni prodotto',
					hint: 'Nel dettaglio prodotto, sopra la condivisione social, mostra quante volte è stato visto. Se è 0 il box non compare',
					icona: 'mdi-eye-outline',
				},
				{
					key: 'showProductPurchases',
					label: 'Prodotti acquistati',
					hint: 'Nel dettaglio prodotto, sopra la condivisione social, mostra quante volte è stato acquistato. Se è 0 il box non compare',
					icona: 'mdi-shopping-outline',
				},
				{
					key: 'enableVoiceSearch',
					label: 'Ricerca vocale',
					hint: "Ricerche vocali dal campo di ricerca e dall'assistente virtuale, in qualunque lingua",
					icona: 'mdi-microphone-outline',
					richiede: 'voice',
				},
				{
					key: 'enableCompareProducts',
					label: 'Confronta prodotti',
					hint: 'Nelle categorie di ultimo livello, sotto Acquista, compare la casella Confronta: il cliente affianca fino a 3 prodotti',
					icona: 'mdi-compare-horizontal',
				},
				{
					key: 'showLowStock',
					label: 'Pezzi disponibili',
					hint: "Nel dettaglio dei prodotti gestiti a quantità compare «Ultimi N pezzi disponibili» quando la giacenza arriva alla soglia. Usa l'unità di misura della variante (es. litri)",
					icona: 'mdi-package-variant-closed-remove',
				},
			],
			etichetteSeo: {
				titleTemplate: { label: 'Modello del titolo', icona: 'mdi-format-title', hint: '%s viene sostituito dal titolo della pagina', largo: true },
				title: { label: 'Titolo del sito', icona: 'mdi-format-header-1' },
				description: { label: 'Descrizione', icona: 'mdi-text-short', largo: true },
				favicon: { label: 'Favicon', icona: 'mdi-star-circle-outline', placeholder: 'https://' },
				og_image: { label: 'Immagine di condivisione', icona: 'mdi-image-outline', placeholder: 'https://' },
			},
			etichetteSocial: {
				facebook: { label: 'Facebook', icona: 'mdi-facebook', placeholder: 'https://facebook.com/...' },
				instagram: { label: 'Instagram', icona: 'mdi-instagram', placeholder: 'https://instagram.com/...' },
				x: { label: 'X (Twitter)', icona: 'mdi-twitter', placeholder: 'https://x.com/...' },
				tiktok: { label: 'TikTok', icona: 'mdi-music-note', placeholder: 'https://tiktok.com/@...' },
				linkedin: { label: 'LinkedIn', icona: 'mdi-linkedin', placeholder: 'https://linkedin.com/...' },
				youtube: { label: 'YouTube', icona: 'mdi-youtube', placeholder: 'https://youtube.com/...' },
				whatsapp: { label: 'WhatsApp', icona: 'mdi-whatsapp', placeholder: 'https://wa.me/39...' },
				telegram: { label: 'Telegram', icona: 'mdi-send-outline', placeholder: 'https://t.me/...' },
				play_store: { label: 'Google Play', icona: 'mdi-google-play', placeholder: 'https://play.google.com/...' },
				apple_store: { label: 'App Store', icona: 'mdi-apple', placeholder: 'https://apps.apple.com/...' },
				threads: { label: 'Threads', icona: 'mdi-at', placeholder: 'https://threads.net/@...' },
			},
			// impronta dell'ultima configurazione salvata (per "modifiche non salvate")
			firma: '',
			// contenuto del popup all'ultimo caricamento/salvataggio: se cambia si rinnova la version
			firmaPopup: '',
			editor: ClassicEditor,
			editorConfig: {
				ckfinder: {
					uploadUrl: '',
				},
				isReadOnly: false,
				focus: false,
			},
			mode_upload: '',
			languages: [],
			searchitem: '',
			isLoadingitem: false,
			listitems: [],
			searchproduct: '',
			types: ['hex', 'hexa', 'rgba', 'hsla', 'hsva'],
			type: 'hex',
			hex: '#FF00FF',
			hexa: '#FF00FFFF',
			rgba: { r: 255, g: 0, b: 255, a: 1 },
			hsla: { h: 300, s: 1, l: 0.5, a: 1 },
			hsva: { h: 300, s: 1, v: 1, a: 1 },
			componentKey: 0,
			rules: [],
			items: [],
			setinterval: '',
			operation: '',
			dropzoneOptionsImage: {
				url: ``,
				thumbnailWidth: 150,
				maxFilesize: 20,
				acceptedFiles: '.jpg,.png,.gif,.ico,.svg',
				headers: '',
				method: 'post',
				//formData.append('userName', 'bob');
			},
			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},
			// ricerca
			settings: {
				search: '',
				filters: {
					status: [],
					platform: [],
					created_at: [],
					payments: [],
				},
				options: {
					sortBy: ['created_at'],
					sortDesc: [true],
					page: 1,
					itemsPerPage: 15,
				},
			},
			uuid: '',
			selected: [],
			loading: false,

			form: {
				layout: {
					product: {
						detail: '',
						view: '',
						variant: true,
					},
					shop: {
						sidebar: '',
						filter: '',
						category: '',
					},
					homepage: [],
					slider: [],
					logo: [],
					color: {},
					color_secondary: {},
					color_third: {},
					color_accent: {},
					theme: {},
					social_links: {},
					script: {
						header: '',
						footer: '',
						addtocart: '',
						buy: '',
						signup: '',
						newsletters: '',
					},
					seo: {
						titleTemplate: '%s - azseller.it',
						title: 'AZ Seller Ecommerce',
						description: 'AZ Seller Website ecommerce',
						favicon: '',
						og_image: '',
					},
					popup: {
						enabled: false,
						delay: 5,
						newsletter: false,
						version: '',
						trans: [],
					},
					cookie_bar: {
						enabled: false,
						block_scripts: true,
						policy_url: '',
					},
				},
				enablePickupStore: false,
				enableCartEnd: false,
				enableMultiCurrency: false,
				enableWIP: false,
				productPlaceholder: false,
				notifyWhenAvailable: false,
				notifyPriceDrop: false,
				showProductViews: false,
				productViewsPeriod: 'today',
				showProductPurchases: false,
				enableVoiceSearch: false,
				enableCompareProducts: false,
				showLowStock: false,
				lowStockThreshold: 5,
				// "HH:MM" o vuoto (box consegna della scheda prodotto)
				deliveryCutoff: '',
			},

			layout: {
				shop: {},
				product: {},
				homepage: [],
				theme: {},
			},
			// temi del sito: Harvest, Boutique e Forge pronti; Breeze e Bazaar arrivano con le loro aree
			temi: [
				{ code: 'harvest', label: 'Harvest', descrizione: 'Lo stile attuale: caldo da bottega, ricerca in evidenza e widget in home.', attivo: true },
				{ code: 'breeze', label: 'Breeze', descrizione: 'Arioso e pulito: menu centrato, foto grandi, sezioni a schede.', attivo: false },
				{ code: 'bazaar', label: 'Bazaar', descrizione: 'Marketplace denso: ricerca larga, menu categorie laterale, griglie fitte.', attivo: false },
				{ code: 'boutique', label: 'Boutique', descrizione: 'Negozio di moda e biancheria: titoli eleganti, foto grandi verticali, ricerca sempre visibile.', attivo: true },
				{ code: 'forge', label: 'Forge', descrizione: 'Ferramenta e forniture industriali: blocchi antracite, rosso e giallo, codici articolo e prezzi + IVA in vista.', attivo: true },
			],
			// vantaggi fissi del sito (home, scheda prodotto): titolo dall'ecommerce, descrizione per lingua da qui
			vantaggi: [
				{ key: 'shippingTime', label: 'Tempi di consegna', predefinito: '24/48h In Italia, 48/72h in Europa in modalità Standard ed Espresso' },
				{ key: 'securePayments', label: 'Pagamenti sicuri', predefinito: 'Paga in totale sicurezza con PayPal, Carta di Credito, Contrassegno o Bonifico Bancario' },
				{ key: 'secureShipping', label: 'Imballaggio sicuro', predefinito: 'Spediamo in tutto il mondo con imballi specifici per i nostri prodotti' },
				{ key: 'customerSupport', label: 'Assistenza clienti rapida', predefinito: 'Forniamo assistenza rapida e veloce tramite Whatsapp, Telefono e Email' },
			],
			// header, footer, slider e newsletter tengono i vecchi codici (tipo 1 = Harvest, tipo 2 = Bazaar)
			header_types: [
				{ code: 'header_1', label: 'Harvest' },
				{ code: 'breeze', label: 'Breeze', disabled: true },
				{ code: 'header_2', label: 'Bazaar' },
				{ code: 'boutique', label: 'Boutique' },
				{ code: 'forge', label: 'Forge' },
			],
			footer_types: [
				{ code: 'footer_1', label: 'Harvest' },
				{ code: 'breeze', label: 'Breeze', disabled: true },
				{ code: 'footer_2', label: 'Bazaar' },
				{ code: 'boutique', label: 'Boutique' },
				{ code: 'forge', label: 'Forge' },
			],
			slider_types: [
				{ code: 'slider_1', label: 'Harvest' },
				{ code: 'slider_2', label: 'Bazaar' },
			],
			newsletter_types: [
				{ code: 'newsletter_1', label: 'Harvest' },
				{ code: 'newsletter_2', label: 'Bazaar' },
			],
			// home, categoria e card prodotto
			area_types: [
				{ code: 'harvest', label: 'Harvest' },
				{ code: 'breeze', label: 'Breeze', disabled: true },
				{ code: 'bazaar', label: 'Bazaar', disabled: true },
				{ code: 'boutique', label: 'Boutique' },
				{ code: 'forge', label: 'Forge' },
			],
			social_links: {
				facebook: '',
				instagram: '',
				x: '',
				tiktok: '',
				linkedin: '',
				youtube: '',
				whatsapp: '',
				telegram: '',
				play_store: '',
				apple_store: '',
				threads: '',
			},
			seo: {
				titleTemplate: '%s - azseller.it',
				title: 'AZ Seller Ecommerce',
				description: 'AZ Seller Website ecommerce',
				favicon: '',
				og_image: '',
			},
		};
	},

	async created() {
		this.setPreload(true);
		this.dropzoneOptionsImage.url = `${this.getUrl}multimedias/uploadimage`;
		this.dropzoneOptionsImage.headers = { Authorization: `Bearer ${this.getToken}` };
		this.editorConfig.ckfinder.uploadUrl = `${this.getUrl}multimedias/ckeditor?token=${this.getToken}`;
		//this.editorConfig.ckfinder.uploadUrl = `${this.getUrl}multimedias/ckeditor?token=${this.getToken}`;
		//console.log(this.getSettings);
		await this.fetchSettings();
		// serve a sapere se il modello voce di OpenAI e' impostato (switch Ricerca vocale)
		await this.getServiceFetch(['openai']);
		this.layout.shop = {
			category: shop.category,
			filter: shop.filter,
			sidebar: shop.sidebar,
		};
		this.layout.product = {
			detail: product.detail,
			view: product.view,
			variant: product.variant,
		};

		let top_header_message = {
			trans: [],
		};

		let note_detail_product = {
			trans: [],
		};

		// Get Languages and Default Languages
		if (this.getSettings.languages !== undefined) {
			this.getSettings.languages = _.orderBy(this.getSettings.languages, ['default'], ['desc']);

			_.forEach(this.getSettings.languages, lang => {
				if (lang.is_active) {
					if (lang.default) {
						this.lang = lang.lang;
					}

					top_header_message.trans.push({
						lang: lang.lang,
						message: '',
					});

					note_detail_product.trans.push({
						lang: lang.lang,
						message: '',
					});
				}
			});
		}

		this.languages = (this.getSettings.languages || [])
			.filter(el => el.is_active)
			.map(el => {
				return {
					lang: el.lang,
				};
			});
		this.layout.theme = {
			header_type: 'header_1',
			footer_type: 'footer_1',
			slider_type: 'slider_1',
			newsletter_type: 'newsletter_1',
			top_header_message: top_header_message,
			note_detail_product: note_detail_product,
		};

		// lo schema crea sempre `frontend` (con i suoi default) ma non `layout`: un profilo nuovo non ha ancora
		// salvato questa pagina, quindi parte dal layout di base
		if (!this.getSettings?.frontend?.layout) {
			this.layout.homepage = homepage;
		} else {
			this.layout.homepage = this.getSettings.frontend.layout.homepage || _.cloneDeep(homepage);
			// fasce aggiunte dopo il salvataggio del profilo: spente, nella posizione del design (dopo la fascia
			// indicata, se c'è; altrimenti in coda). Le offerte bundle il sito le mostrava già dopo i brand:
			// entrano accese, così non cambia nulla finché non si spengono
			const dopo = { h16: 'h11', h18: 'h6', h12: 'h18', h13: 'h12', h14: 'h13', h15: 'h2', h17: 'h8' };
			homepage.forEach(fascia => {
				if (this.layout.homepage.find(el => el.code === fascia.code)) return;
				const nuova = { ...fascia, active: fascia.code === 'h18' };
				const prima = this.layout.homepage.findIndex(el => el.code === dopo[fascia.code]);
				if (prima < 0) this.layout.homepage.push(nuova);
				else this.layout.homepage.splice(prima + 1, 0, nuova);
			});
			this.form.layout = {
				shop: {
					category: this.getSettings.frontend.layout.shop?.category,
					filter: this.getSettings.frontend.layout.shop?.filter,
					sidebar: this.getSettings.frontend.layout.shop?.sidebar,
				},
				product: {
					detail: this.getSettings.frontend.layout.product?.detail,
					view: this.getSettings.frontend.layout.product?.view,
					variant: this.getSettings.frontend.layout.product?.variant,
				},
				slider: this.getSettings.frontend.layout?.slider,
				logo: this.getSettings.frontend.layout?.logo,
				color: this.getSettings.frontend.layout?.color,
				color_secondary: this.getSettings.frontend.layout?.color_secondary,
				color_third: this.getSettings.frontend.layout?.color_third,
				color_accent: this.getSettings.frontend.layout?.color_accent,
				theme: this.getSettings.frontend.layout?.theme ? this.getSettings.frontend.layout?.theme : this.layout.theme,
				social_links: this.getSettings.frontend.layout?.social_links ? this.getSettings.frontend.layout?.social_links : this.social_links,
				seo: this.getSettings.frontend.layout?.seo ? this.getSettings.frontend.layout?.seo : this.seo,
				script: {
					header: this.getSettings.frontend.layout?.script?.header ? decode(this.getSettings.frontend.layout?.script?.header) : '',
					footer: this.getSettings.frontend.layout?.script?.footer ? decode(this.getSettings.frontend.layout?.script?.footer) : '',
					addtocart: this.getSettings.frontend.layout?.script?.addtocart ? decode(this.getSettings.frontend.layout?.script?.addtocart) : '',
					buy: this.getSettings.frontend.layout?.script?.buy ? decode(this.getSettings.frontend.layout?.script?.buy) : '',
					signup: this.getSettings.frontend.layout?.script?.signup ? decode(this.getSettings.frontend.layout?.script?.signup) : '',
					newsletters: this.getSettings.frontend.layout?.script?.newsletters ? decode(this.getSettings.frontend.layout?.script?.newsletters) : '',
				},
				popup: this.form.layout.popup,
				cookie_bar: this.form.layout.cookie_bar,
			};
			const cookieBar = this.getSettings.frontend.layout?.cookie_bar;
			if (cookieBar) {
				Object.assign(this.form.layout.cookie_bar, {
					enabled: !!cookieBar.enabled,
					block_scripts: cookieBar.block_scripts !== false,
					policy_url: cookieBar.policy_url || '',
				});
			}
			const popup = this.getSettings.frontend.layout?.popup;
			if (popup) {
				Object.assign(this.form.layout.popup, {
					enabled: !!popup.enabled,
					delay: Number.isFinite(Number(popup.delay)) ? Number(popup.delay) : 5,
					newsletter: !!popup.newsletter,
					version: popup.version || '',
					// xss-clean salva l'HTML codificato
					trans: (popup.trans || []).map(t => ({ lang: t.lang, text: t.text ? decode(t.text) : '' })),
				});
			}

			this.form.layout.theme.header_type = this.getSettings.frontend?.layout?.theme?.header_type ? this.getSettings.frontend.layout.theme.header_type : 'header_1';
			this.form.layout.theme.footer_type = this.getSettings.frontend?.layout?.theme?.footer_type ? this.getSettings.frontend.layout.theme.footer_type : 'footer_1';
			this.form.layout.theme.slider_type = this.getSettings.frontend?.layout?.theme?.slider_type ? this.getSettings.frontend.layout.theme.slider_type : 'slider_1';
			this.form.layout.theme.newsletter_type = this.getSettings.frontend?.layout?.theme?.newsletter_type ? this.getSettings.frontend.layout.theme.newsletter_type : 'newsletter_1';
			this.form.layout.theme.note_detail_product = this.getSettings.frontend?.layout?.theme?.note_detail_product ? this.getSettings.frontend.layout.theme.note_detail_product : note_detail_product;
		}
		// opzioni del sito: stanno in `frontend` anche quando il layout non e' mai stato salvato
		const fe = this.getSettings?.frontend || {};
		this.form.enablePickupStore = fe.enablePickupStore || false;
		this.form.enableCartEnd = fe.enableCartEnd || false;
		this.form.enableMultiCurrency = fe.enableMultiCurrency || false;
		this.form.enableWIP = fe.enableWIP || false;
		this.form.productPlaceholder = fe.productPlaceholder || false;
		this.form.notifyWhenAvailable = fe.notifyWhenAvailable || false;
		this.form.notifyPriceDrop = fe.notifyPriceDrop || false;
		this.form.showProductViews = fe.showProductViews || false;
		this.form.productViewsPeriod = fe.productViewsPeriod === '30d' ? '30d' : 'today';
		this.form.showProductPurchases = fe.showProductPurchases || false;
		// senza modello voce la funzione non puo' girare: lo switch resta spento
		this.form.enableVoiceSearch = (fe.enableVoiceSearch && this.voiceDisponibile) || false;
		this.form.enableCompareProducts = fe.enableCompareProducts || false;
		this.form.showLowStock = fe.showLowStock || false;
		this.form.lowStockThreshold = fe.lowStockThreshold >= 1 ? fe.lowStockThreshold : 5;
		this.form.deliveryCutoff = fe.deliveryCutoff || '';
		// tema a varianti: campi aggiunti dopo, se mancano valgono Harvest (come nell'ecommerce)
		['preset', 'home_type', 'category_type', 'card_type'].forEach(k => {
			if (!this.form.layout.theme[k]) this.$set(this.form.layout.theme, k, 'harvest');
		});
		// descrizioni dei vantaggi: una voce per lingua attiva, vuota = testo predefinito dell'ecommerce
		const perks = this.form.layout.theme.perks || {};
		this.$set(
			this.form.layout.theme,
			'perks',
			this.vantaggi.reduce((acc, v) => {
				const trans = (perks[v.key] && perks[v.key].trans) || [];
				acc[v.key] = { trans: this.languages.map(l => trans.find(t => t.lang === l.lang) || { lang: l.lang, message: '' }) };
				return acc;
			}, {}),
		);
		// contenuti della home Boutique: ogni testo con una voce per lingua attiva, foto e indirizzi senza lingua
		this.$set(this.form.layout.theme, 'boutique', this.contenutiBoutique(this.form.layout.theme.boutique));
		// una voce di testo per ogni lingua attiva, la predefinita per prima
		const testiPopup = this.form.layout.popup.trans;
		this.form.layout.popup.trans = this.languages.map(l => testiPopup.find(t => t.lang === l.lang) || { lang: l.lang, text: '' });
		this.setPreload(false);
		this.selectedchecbox = [];
		this.$nextTick(() => {
			this.firma = this.firmaAttuale();
			this.firmaPopup = this.firmaPopupAttuale();
		});
	},
	mounted() {
		// sezione attiva nell'indice mentre si scorre
		this.osservatore = new IntersectionObserver(
			voci => {
				const visibile = voci.filter(v => v.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
				if (visibile) this.attiva = visibile.target.id;
			},
			{ rootMargin: '-140px 0px -60% 0px' },
		);
		this.sezioni.forEach(s => {
			const el = document.getElementById(s.id);
			if (el) this.osservatore.observe(el);
		});
	},

	watch: {
		settings: {},
		async searchproduct(val) {
			if (val) {
				const item = await this.getDetailItem({ id: val._id });

				const trans = item.data.data.trans.map(el => {
					return {
						lang: el.lang,
						name: el.name,
						text: '',
						badge: '',
					};
				});
				const slugs = item.data.data.slugs.map(el => {
					return {
						lang: el.lang,
						url: `${this.getSettings.website}/${el.lang}/${el.text}`,
					};
				});
				this.form.layout.slider.push({ sizes: val?.images?.[0]?.sizes, trans: trans, url: slugs });
			}
		},
		searchitem: _.debounce(function (val) {
			if (val == null || val === '' || val.length < 3) return;

			this.isLoading = true;
			let setting = {
				options: {
					sortBy: ['created_at'],
					sortDesc: [true],
					page: 1,
					itemsPerPage: 15,
				},
				filters: '',
				search: val,
			};
			///console.log("setting",setting)
			let items = this.fetchProducts(setting);
			items
				.then(result => {
					this.listitems = result;

					//this.form.layout.slider.push({ sizes: images,text:'',url:'' });
				})

				.finally(() => (this.isLoading = false));
		}, 200),

		//searchitem(val){
	},
	components: {
		TourOverlay,
		KeyValueCard,
		SectionCard,
		LogoColori,
		SliderHome,
		BlocchiHome,
		SceltaTema,
		TemaPreset,
		TestiLingua,
		ContenutiBoutique,
		CampiLink,
		ScriptAzioni,
		PopupApertura,
		CookieBar,
		vueDropzone: vue2Dropzone,
		Table,
		FormCreate,
		Draggable,
		//VuetifyDraggableTreeview
	},
	computed: {
		modificato() {
			return !!this.firma && this.firmaAttuale() !== this.firma;
		},
		contaSlide() {
			const n = (this.form.layout && this.form.layout.slider && this.form.layout.slider.length) || 0;
			return n === 1 ? '1 slide' : `${n} slide`;
		},
		// qualche area del tema e' stata cambiata in "Avanzate"
		// contenuti Boutique: il pannello compare se la home (o tutto il tema) usa Boutique
		usaBoutique() {
			const theme = this.form.layout.theme || {};
			return theme.preset === 'boutique' || theme.home_type === 'boutique';
		},
		etichettaCaricamento() {
			const blocco = BLOCCHI_BOUTIQUE.find(b => 'boutique.' + b.key === this.mode_upload);
			return blocco ? 'foto: ' + blocco.label.toLowerCase() : this.mode_upload;
		},
		temaPersonalizzato() {
			const theme = this.form.layout.theme || {};
			const attese = AREE_TEMA[theme.preset] ? areeTema(theme.preset) : {};
			return Object.keys(attese).some(k => theme[k] !== attese[k]);
		},
		// ordine consigliato delle fasce (quello del tema, in utils/layout.js)
		ordineTema() {
			return homepage.map(f => f.code);
		},
		contaFasce() {
			const tutte = this.layout.homepage || [];
			return `${tutte.filter(f => f.active).length} visibili su ${tutte.length}`;
		},
		statoPopup() {
			const p = this.form.layout.popup;
			if (!p.enabled) return 'disattivato';
			return `dopo ${p.delay || 0} s${p.newsletter ? ' · con newsletter' : ''}`;
		},
		statoCookie() {
			const c = this.form.layout.cookie_bar;
			if (!c.enabled) return 'disattivata';
			return c.block_scripts ? 'attiva · script bloccati fino al consenso' : 'attiva · solo informativa';
		},
		contaSocial() {
			const v = (this.form.layout && this.form.layout.social_links) || {};
			const n = Object.keys(this.social_links).filter(k => String(v[k] || '').trim()).length;
			return `${n} collegati`;
		},
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		selectedLength() {
			return this.selectedchecbox.length;
		},
		// ricerca vocale: serve OpenAI attivo con la chiave e il modello voce
		voiceDisponibile() {
			const openai = (this.getServices || []).find(s => s.service === 'openai');
			return !!(openai && openai.active && openai.setting?.key && openai.setting?.voice);
		},
		...mapGetters({
			getServices: 'services/getServices',
			getAutenticate: 'profile/getAutenticate',
			getToken: 'profile/getToken',
			getId: 'profile/getId',
			getUrl: 'settings/getUrl',
			getLanguage: 'profile/getLanguage',
			getDialog: 'settings/getDialog',
			getPreload: 'settings/getPreload',
			getSnackbar: 'snackbars/getSnackbar',
			getColor: 'snackbars/getColor',
			getMultiline: 'snackbars/getMultiline',
			getTimeout: 'snackbars/getTimeout',
			getVertical: 'snackbars/getVertical',
			getText: 'snackbars/getText',
			getSettings: 'settings/getSetting',
			getDrawerFilter: 'settings/getDrawerFilter',
		}),

		navStateFilter: {
			get: function () {
				return this.getDrawerFilter;
			},
			set: function () {
				return this.getDrawerFilter;
			},
		},
	},

	methods: {
		// il tema imposta tutte le aree insieme (anche quelle cambiate in "Avanzate")
		scegliTema(tema) {
			const aree = { preset: tema, ...areeTema(tema) };
			Object.keys(aree).forEach(k => this.$set(this.form.layout.theme, k, aree[k]));
		},
		...mapMutations({
			setLogout: 'profile/setLogout',
			setDialog: 'settings/setDialog',
			setPreload: 'settings/setPreload',
			setSnackbar: 'snackbars/setSnackbar',
			setClearError: 'settings/setClearError',
			setMessages: 'settings/setMessages',
			setExistFilter: 'settings/setExistFilter',
			setDrawerFilter: 'settings/setDrawerFilter',
		}),
		...mapActions({
			getServiceFetch: 'services/getServiceFetch',
			fetchSettings: 'settings/asyncFetchData',
			fetchStatusOrder: 'statusorders/fetchLists',
			destroyData: 'settings/destroyData',
			setSettings: 'settings/updateData',
			createBarcode: 'barcode/createBarcode',
			fetchProducts: 'items/asyncSearchData',
			getDetailItem: 'items/getDetailItem',

			//'resetMessages' : 'settings/resetMessages'
		}),
		openModalUpload(mode) {
			this.mode_upload = mode;
			this.dialog_upload = true;
		},
		createImage(item) {
			let image = '';

			if (_.size(item.images) > 0) {
				let first = _.first(item.images);

				if (_.size(first.sizes) > 0) {
					let firstimage = _.first(first.sizes);
					image = firstimage.value;
				}
			}
			let images = item.images;

			if (_.size(item.variants) > 0) {
				_.forEach(item.variants.variants, variant => {
					_.forEach(images, img => {
						if (_.includes(img.variants, _.toString(variant._id))) {
							let first = _.first(img.sizes);
							image = first.value;
							return false;
						}
					});
				});
			}
			try {
				return image;
			} catch (e) {
				return require(`@/assets/images/picture.svg`);
			}
			//return image;
			//require(`@/assets/images/picture.svg`)
		},
		uploadCompleteImage(file) {
			if (file.status === 'success') {
				let value = JSON.parse(file.xhr.response);
				let images = [
					{
						value: `${value.url}`,
						size: file.width,
					},
				];

				const url = new URL(value.url);

				let splitUrl = _.split(url.pathname, '/');
				_.forEach(this.getSettings.thumbnails, el => {
					let work = _.clone(splitUrl);
					work.splice(4, 0, el.size);
					work = _.join(work, '/');
					let urlComplete = `${url.origin}${work}`;
					images = [
						...images,
						{
							value: `${urlComplete}`,
							size: el.size,
						},
					];
				});
				const trans = [];
				const urls = [];
				for (const lang of this.languages) {
					trans.push({ lang: lang.lang, name: '', text: '', badge: '', url: '' });
					urls.push({ lang: lang.lang, url: '' });
				}
				this.form.layout.slider.push({ sizes: images, trans: trans, url: urls });
			}
		},
		uploadCompleteMedia(file) {
			if (file.status === 'success') {
				let value = JSON.parse(file.xhr.response);
				// foto dei blocchi Boutique: mode_upload = 'boutique.<blocco>'
				if (String(this.mode_upload).startsWith('boutique.')) {
					const blocco = this.form.layout.theme.boutique[this.mode_upload.slice('boutique.'.length)];
					if (blocco) blocco.image = `${value.url}`;
					this.dialog_upload = false;
					return;
				}
				switch (this.mode_upload) {
					case 'favicon': {
						this.form.layout.seo.favicon = `${value.url}`;
						break;
					}
					case 'og_image': {
						this.form.layout.seo.og_image = `${value.url}`;
						break;
					}
				}
			}
		},
		uploadCompleteLogo(file) {
			if (file.status === 'success') {
				let value = JSON.parse(file.xhr.response);
				let images = [
					{
						value: `${value.url}`,
						size: file.width,
					},
				];

				const url = new URL(value.url);

				let splitUrl = _.split(url.pathname, '/');
				_.forEach(this.getSettings.thumbnails, el => {
					let work = _.clone(splitUrl);
					work.splice(4, 0, el.size);
					work = _.join(work, '/');
					let urlComplete = `${url.origin}${work}`;
					images = [
						...images,
						{
							value: `${urlComplete}`,
							size: el.size,
						},
					];
				});

				this.form.layout.logo = [{ sizes: images }];
			}
		},
		sendingEvent(file, xhr, formData) {
			formData.append('uuid', this.uuid);
			formData.append('type', 'file');
			formData.append('folder', 'items');
			//  formData.append('token', this.getToken);
		},
		saveSetting() {
			this.setPreload(true);
			this.form.layout.homepage = this.layout.homepage;
			// soglia vuota o sotto 1: torna al valore predefinito
			const soglia = parseInt(this.form.lowStockThreshold, 10);
			this.form.lowStockThreshold = soglia >= 1 ? soglia : 5;
			// orario limite: solo "HH:MM" valido, altrimenti vuoto
			this.form.deliveryCutoff = /^([01]\d|2[0-3]):[0-5]\d$/.test(this.form.deliveryCutoff || '') ? this.form.deliveryCutoff : '';
			// popup cambiato: nuova version, cosi' ricompare anche a chi aveva chiuso il precedente
			const firmaPopup = this.firmaPopupAttuale();
			if (firmaPopup !== this.firmaPopup || !this.form.layout.popup.version) {
				this.form.layout.popup.version = String(Date.now());
			}

			let payload = this.form;

			Object.assign(payload, { type: 'layout' });
			let url = 'profile/update/frontend';
			axios
				.post(`${this.getUrl}${url}`, payload, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(({ data }) => {
					//   this.dialog = false;
					//   this.success = true;
					//   console.log(data.data);
					this.setSettings(data.data);
					this.firma = this.firmaAttuale();
					this.firmaPopup = this.firmaPopupAttuale();
					this.setPreload(false);
					this.snackbar.color = 'success';
					this.snackbar.text = 'Operazione effettuata con  successo';
					this.snackbar.snackbar = true;
					this.snackbar.mode = 'multi-line';
					this.snackbar.timeout = 5000;
				})
				.catch(err => {
					if (err.response) {
						let errors = err.response.data.error;
						if (errors) {
							if (_.isArray(errors)) {
								errors.forEach(value => {
									//console.log(value.msg);
									this.messages[value.param] = value.msg;
								});
							} else {
								err.message = errors;
							}
						}

						this.snackbar.color = 'error';
						this.snackbar.text = err.message;
						this.snackbar.snackbar = true;
						this.snackbar.mode = 'multi-line';
						this.snackbar.timeout = 5000;
						this.preload = false;
						if (err.response.status === 401) {
							this.setLogout();
							this.$router.push('/login');
						}
					}
				});

			//console.log(`${this.getUrl}${url}`);
		},
		firmaAttuale() {
			return JSON.stringify([this.form, this.layout.homepage]);
		},
		firmaPopupAttuale() {
			const { enabled, delay, newsletter, trans } = this.form.layout.popup;
			return JSON.stringify([enabled, delay, newsletter, trans]);
		},
		translatePopup(id, lang) {
			let formData = {
				target: lang,
				words: {
					title: this.form.layout.popup.trans[id].text,
				},
			};

			axios
				.post(`${this.getUrl}translates`, { data: formData }, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(res => {
					this.form.layout.popup.trans[id].text = decodeURIComponent(res.data.data[0]);
				})
				.catch(err => {
					console.log(err);
				});
		},
		copyTranslatePopup(id) {
			this.form.layout.popup.trans[id].text = this.form.layout.popup.trans[0].text;
		},
		vaiA(id) {
			const el = document.getElementById(id);
			if (!el) return;
			this.attiva = id;
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		},
		deleteDropLogo(index) {
			this.form.layout.logo.splice(index, 1);
		},
		deleteDrop(index) {
			this.form.layout.slider.splice(index, 1);
		},
		async updateDropSlider(dropResult) {
			this.oldValue = this.form.layout.slider[dropResult.moved.oldIndex];
			this.newValue = this.form.layout.slider[dropResult.moved.newIndex];

			this.form.layout.slider.splice(dropResult.moved.oldIndex, 1);
			this.form.layout.slider.splice(dropResult.moved.newIndex, 0, this.oldValue);

			//this.form.values[dropResult.moved.newIndex]=this.oldValue
			//this.form.values[dropResult.moved.oldIndex]=this.newValue
		},
		// fasce nell'ordine del tema; quelle che il tema non conosce restano in coda nel loro ordine.
		// Accese/spente non cambiano; si salva col pulsante in basso
		ordinaComeTema() {
			const pos = code => {
				const i = this.ordineTema.indexOf(code);
				return i < 0 ? Infinity : i;
			};
			const ordinate = this.layout.homepage.map((f, i) => ({ f, i })).sort((a, b) => pos(a.f.code) - pos(b.f.code) || a.i - b.i);
			this.layout.homepage.splice(0, this.layout.homepage.length, ...ordinate.map(o => o.f));
		},
		async updateDrop(dropResult) {
			this.oldValue = this.layout.homepage[dropResult.moved.oldIndex];
			this.newValue = this.layout.homepage[dropResult.moved.newIndex];

			this.layout.homepage.splice(dropResult.moved.oldIndex, 1);
			this.layout.homepage.splice(dropResult.moved.newIndex, 0, this.oldValue);

			//this.form.values[dropResult.moved.newIndex]=this.oldValue
			//this.form.values[dropResult.moved.oldIndex]=this.newValue
		},
		propSetMessages(text) {
			this.conditions = this.getSettings.condition;
			this.snackbar.color = 'success';
			this.snackbar.text = text;
			this.snackbar.snackbar = true;
			this.snackbar.mode = 'multi-line';
			this.snackbar.timeout = 5000;
			this.operation = '';
		},

		onResize() {
			this.isMobile = window.innerWidth < 769;
		},
		toggleDrawerFilter() {
			this.setDrawerFilter(!this.getDrawerFilter);
			this.clippedOrder = 0;
			//this.$store.commit('setDrawer') = !this.$store.state.drawer;
		},
		toggle() {
			if (!this.selectedall) {
				this.selectedchecbox = [];
			} else {
				//console.log(this.selected[0])

				this.selectedchecbox = [];
				_.forEach(this.conditions, cond => {
					if (!cond.default) {
						this.selectedchecbox.push(cond._id);
					}
				});
			}
		},
		async confirm() {
			let payload = this.form;
			try {
				this.preload = true;
				await this.fetchUsers(payload);
			} catch (err) {
				console.log(err);
			}
			this.preload = false;
		},
		translateItem(id, lang) {
			var formData = {
				target: lang,
				words: {
					title: this.form.layout.theme.top_header_message.trans[id].message,
				},
			};

			axios
				.post(`${this.getUrl}translates`, { data: formData }, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(res => {
					this.form.layout.theme.top_header_message.trans[id].message = decodeURIComponent(res.data.data[0]);
				})
				.catch(err => {
					console.log(err);
				});
		},
		copyTranslate(id) {
			this.form.layout.theme.top_header_message.trans[id].message = this.form.layout.theme.top_header_message.trans[0].message;
		},
		translateItemNote(id, lang) {
			let formData = {
				target: lang,
				words: {
					title: this.form.layout.theme.note_detail_product.trans[id].message,
				},
			};

			axios
				.post(`${this.getUrl}translates`, { data: formData }, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(res => {
					this.form.layout.theme.note_detail_product.trans[id].message = decodeURIComponent(res.data.data[0]);
				})
				.catch(err => {
					console.log(err);
				});
		},
		contenutiBoutique(salvati = {}) {
			const testo = (vecchio = {}) => ({ trans: this.languages.map(l => (vecchio.trans || []).find(t => t.lang === l.lang) || { lang: l.lang, message: '' }) });
			const out = { topbar_left: testo(salvati.topbar_left), topbar_right: testo(salvati.topbar_right) };
			BLOCCHI_BOUTIQUE.forEach(b => {
				const vecchio = salvati[b.key] || {};
				out[b.key] = { image: vecchio.image || '' };
				b.campi.forEach(c => (out[b.key][c.key] = testo(vecchio[c.key])));
				b.link.forEach(l => (out[b.key][l.key] = vecchio[l.key] || ''));
			});
			return out;
		},
		// testo in tutte le lingue ({ lang, message }): copia dalla prima lingua o traduzione automatica
		copiaTesto(trans, id) {
			trans[id].message = trans[0].message;
		},
		traduciTesto(trans, id, lang) {
			if (!trans[0].message) return;
			axios
				.post(`${this.getUrl}translates`, { data: { target: lang, words: { title: trans[0].message } } }, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(res => {
					trans[id].message = decodeURIComponent(res.data.data[0]);
				})
				.catch(err => {
					console.log(err);
				});
		},
		copiaVantaggio(key, id) {
			const trans = this.form.layout.theme.perks[key].trans;
			trans[id].message = trans[0].message;
		},
		traduciVantaggio(key, id, lang) {
			const trans = this.form.layout.theme.perks[key].trans;
			axios
				.post(`${this.getUrl}translates`, { data: { target: lang, words: { title: trans[0].message } } }, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(res => {
					trans[id].message = decodeURIComponent(res.data.data[0]);
				})
				.catch(err => {
					console.log(err);
				});
		},
		copyTranslateNote(id) {
			this.form.layout.theme.note_detail_product.trans[id].message = this.form.layout.theme.note_detail_product.trans[0].message;
		},
		getImageFlag(img) {
			img = _.upperCase(img);
			try {
				return require(`@/assets/images/flag/${img}.svg`);
			} catch (e) {
				return require(`@/assets/images/flag/map.svg`);
			}
		},
	},
	// `beforeUnmount` qui sotto e il nome dell'hook di Vue 3: su Vue 2 non viene mai
	// chiamato, quindi i listener del tutorial vanno staccati da `beforeDestroy`.
	beforeDestroy() {
		this.tour.stop();
		if (this.osservatore) this.osservatore.disconnect();
	},
	beforeUnmount() {
		this.destroyData();

		//console.log('Main Vue destroyed')
	},
};
</script>

<style></style>

<style scoped>
.ly-page {
	max-width: 1600px;
	padding-bottom: 0;
}
.ly-top {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 12px;
}
.ly-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2d3d;
}
.ly-sub,
.ly-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.ly-tema-testi {
	margin-top: 20px;
}
.ly-avanzate {
	margin-top: 16px;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
}
/* indice delle sezioni: resta sotto la barra dell'app mentre si scorre */
.ly-nav {
	position: sticky;
	top: 48px;
	z-index: 4;
	display: flex;
	gap: 6px;
	overflow-x: auto;
	padding: 8px 0;
	margin-bottom: 8px;
	background: #fff;
	border-bottom: 1px solid #eef2f7;
}
.ly-chip {
	flex: 0 0 auto;
}
.ly-sections {
	display: flex;
	flex-direction: column;
	gap: 16px;
}
.ly-sections ::v-deep .section-card {
	height: auto;
	scroll-margin-top: 110px;
}
.ly-switches {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 10px;
}
.ly-switch {
	display: flex;
	align-items: center;
	padding: 10px 12px;
	border: 1px solid #e1e7ef;
	border-radius: 10px;
	background: #fff;
}
.ly-switch--on {
	border-color: #9fb6d3;
	background: #f7faff;
}
.ly-switch-text {
	flex: 1 1 auto;
	min-width: 0;
}
.ly-soglia {
	max-width: 240px;
}
.ly-consegna {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	row-gap: 8px;
	margin-top: 16px;
	padding: 10px 12px;
	border: 1px solid #e1e7ef;
	border-radius: 10px;
}
.ly-switch-label {
	font-size: 14px;
	font-weight: 600;
	color: #1f2d3d;
}
.ly-requisito {
	margin-top: 4px;
	font-size: 12px;
	color: #b26a00;
}
/* barra di salvataggio: sempre in fondo alla vista */
.ly-savebar {
	position: sticky;
	bottom: 0;
	z-index: 4;
	display: flex;
	align-items: center;
	margin: 16px -12px 0;
	padding: 10px 16px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
	box-shadow: 0 -4px 12px rgba(31, 45, 61, 0.06);
}
.ly-savebar--dirty {
	background: #fffaf0;
}
.ly-savebar-text {
	font-size: 13px;
	color: #1f2d3d;
}
.ly-dialog-title {
	font-size: 17px;
	font-weight: 600;
}
@media (max-width: 768px) {
	.ly-switches {
		grid-template-columns: 1fr;
	}
}
</style>
